if (!customElements.get('most-popular-slider')) {
  customElements.define(
    'most-popular-slider',
    class MostPopularSlider extends HTMLElement {
      connectedCallback() {
        this.scroller = this.querySelector('.most-popular__slider');
        this.dotsWrap = this.querySelector('.most-popular__dots');
        if (!this.scroller || !this.dotsWrap) return;

        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        this.buildDots();
        this.enableDrag();
        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(this.scroller);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        cancelAnimationFrame(this.momentumRaf);
        cancelAnimationFrame(this.scrollRaf);
        clearTimeout(this.resizeTimer);
      }

      enableDrag() {
        const scroller = this.scroller;
        let startX = 0;
        let startScroll = 0;
        let lastX = 0;
        let velocity = 0;
        let dragging = false;
        let moved = false;

        scroller.addEventListener('dragstart', (e) => e.preventDefault());

        scroller.addEventListener('pointerdown', (e) => {
          if (e.pointerType !== 'mouse') return;
          dragging = true;
          moved = false;
          startX = lastX = e.clientX;
          startScroll = scroller.scrollLeft;
          velocity = 0;
          cancelAnimationFrame(this.momentumRaf);
        });

        scroller.addEventListener('pointermove', (e) => {
          if (!dragging) return;
          const delta = e.clientX - startX;
          if (!moved && Math.abs(delta) > 5) {
            moved = true;
            scroller.setPointerCapture(e.pointerId);
          }
          if (moved) {
            velocity = e.clientX - lastX;
            scroller.scrollLeft = startScroll - delta;
          }
          lastX = e.clientX;
        });

        const endDrag = () => {
          if (!dragging) return;
          dragging = false;
          if (moved) this.momentum(-velocity);
        };
        scroller.addEventListener('pointerup', endDrag);
        scroller.addEventListener('pointercancel', endDrag);

        scroller.addEventListener(
          'click',
          (e) => {
            if (moved) {
              e.preventDefault();
              e.stopPropagation();
              moved = false;
            }
          },
          true
        );
      }

      momentum(initialVelocity) {
        let v = initialVelocity;
        const step = () => {
          if (Math.abs(v) < 0.5) return;
          this.scroller.scrollLeft += v;
          v *= 0.94;
          this.momentumRaf = requestAnimationFrame(step);
        };
        this.momentumRaf = requestAnimationFrame(step);
      }

      metrics() {
        const slides = this.scroller.querySelectorAll('.most-popular__slide');
        if (!slides.length) return null;

        const styles = getComputedStyle(this.scroller);
        const gap = parseFloat(styles.columnGap) || 0;
        const inner =
          this.scroller.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
        const step = slides[0].getBoundingClientRect().width + gap;
        if (step <= 0) return null;

        return {
          step: step,
          total: slides.length,
          perView: Math.max(1, Math.floor((inner + gap) / step + 0.001)),
        };
      }

      buildDots() {
        const metrics = this.metrics();
        this.cachedMetrics = metrics;
        this.dotsWrap.replaceChildren();
        if (!metrics) return;

        const pages = Math.max(1, metrics.total - metrics.perView + 1);
        if (pages < 2) return;

        const slideLabel = this.dotsWrap.dataset.slideLabel || 'Load slide';
        const ofLabel = this.dotsWrap.dataset.slideOf || 'of';
        const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;

        for (let i = 0; i < pages; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'most-popular__dot';
          dot.setAttribute('aria-label', `${slideLabel} ${i + 1} ${ofLabel} ${pages}`);
          dot.addEventListener('click', () => {
            this.scroller.scrollTo({ left: Math.min(i * metrics.step, maxScroll), behavior: 'smooth' });
          });
          this.dotsWrap.appendChild(dot);
        }
        this.updateActiveDot();
      }

      updateActiveDot() {
        const dots = this.dotsWrap.children;
        if (!dots.length || !this.cachedMetrics) return;

        const active = Math.min(dots.length - 1, Math.round(this.scroller.scrollLeft / this.cachedMetrics.step));
        for (let i = 0; i < dots.length; i++) {
          const isActive = i === active;
          dots[i].classList.toggle('is-active', isActive);
          if (isActive) {
            dots[i].setAttribute('aria-current', 'true');
          } else {
            dots[i].removeAttribute('aria-current');
          }
        }
      }

      onScroll() {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = null;
          this.updateActiveDot();
        });
      }

      onResize() {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.buildDots(), 150);
      }
    }
  );
}
